/**
 * CLE-2811 (2026-09-28) — "Save draft", the facts run's last button (Ani: "It should be Save Draft").
 *
 * One click saves every contract term she has not rejected onto a contract, the way the per-term save
 * panel would, fill-only (`replace` off). Two kinds are left alone and named in the toast instead: a term
 * she OVERRODE (accepting it would write the proposal she disagreed with — she saves it from its card),
 * and a term that still needs input (`missingInputs`), which cannot be accepted meaningfully.
 *
 *   1. accept the term — with her unsaved edits from its card — unless it is already accepted as shown;
 *   2. dry-run the save to learn where it goes (`target`);
 *   3. save it there: the run's contract, the contract it was already saved into (`alreadySavedTo`), the
 *      one existing contract for the term's payer, or a new draft contract when the payer has none
 *      (`createNewContract`). A term whose payer has SEVERAL existing contracts and was never saved into
 *      one is skipped — which one is her call — and its card is highlighted.
 *
 * The planner (which terms, where each goes, what to say afterwards) is pure; `runSaveDraft` sequences
 * the calls with the mutations injected, so both are tested without a DOM or a store.
 */

import {
  IAcceptRequest,
  IApplyContractTermRequest,
  IApplyContractTermResult,
  IDraftRecommendation,
} from "../../../store/queries/contractModelDrafts";
import { missingInputsOf } from "../utils";
import { planWritesSomething } from "./applyTermPreview";

/** A contract term Save draft works through. */
export interface ISaveDraftTerm {
  id: string;
  /** What the reviewer reads for its payer (the page's resolver — never an id). */
  payerName: string;
  /** Accept it first: it is not accepted yet, or her card holds unsaved edits. */
  accept: boolean;
  /** Her unsaved edits, sent with the accept. Undefined when she made none. */
  edits?: Record<string, unknown>;
}

const isWithdrawn = (r: IDraftRecommendation): boolean =>
  r.status === "superseded" || r.changeKind === "superseded";

/** A contract term still in play: not rejected by her, not withdrawn by the agent. */
const isLiveTerm = (r: IDraftRecommendation): boolean =>
  r.componentType === "contract_term" &&
  r.status !== "rejected" &&
  !isWithdrawn(r);

/** Why Save draft leaves a live contract term alone. */
export type SaveDraftSkipReason = "overridden" | "needs_input";

/**
 * - `overridden`: she disagreed and left structured feedback. Accepting it would flip it to accepted and
 *   write the agent's proposal onto the contract — the very thing she overrode.
 * - `needs_input` with something listed in `missingInputs`: the agent could not model it without an answer
 *   she has not given; accepting it would save a term with holes. (With nothing listed it is saved.)
 */
const skipReasonOf = (r: IDraftRecommendation): SaveDraftSkipReason | null => {
  if (r.status === "overridden") return "overridden";
  if (r.status === "needs_input" && missingInputsOf(r).length > 0)
    return "needs_input";
  return null;
};

/**
 * The terms Save draft saves, in page order: every contract term she has not rejected (and the agent has
 * not withdrawn), except one she overrode or one that still needs input (`saveDraftSkips`). One she
 * already accepted is accepted again only when its card holds edits she has not saved — what she sees is
 * what gets written.
 */
export const saveDraftTerms = (
  recs: IDraftRecommendation[],
  edits: Record<string, Record<string, unknown> | null | undefined>,
  payerNameOf: (rec: IDraftRecommendation) => string
): ISaveDraftTerm[] =>
  recs
    .filter((r) => isLiveTerm(r) && skipReasonOf(r) === null)
    .map((r) => {
      const own = edits[r.id] ?? undefined;
      return {
        id: r.id,
        payerName: payerNameOf(r),
        accept: r.status !== "accepted" || own !== undefined,
        ...(own ? { edits: own } : {}),
      };
    });

/**
 * The live contract terms Save draft leaves alone, in page order, as outcomes for the toast: never
 * accepted, never dry-run, never written.
 */
export const saveDraftSkips = (
  recs: IDraftRecommendation[],
  payerNameOf: (rec: IDraftRecommendation) => string
): SaveDraftOutcome[] =>
  recs.filter(isLiveTerm).flatMap((r): SaveDraftOutcome[] => {
    const reason = skipReasonOf(r);
    return reason
      ? [{ kind: reason, recId: r.id, payerName: payerNameOf(r) }]
      : [];
  });

/** Where one term's save goes, decided from its dry run. */
export type SaveDraftStep =
  | {
      kind: "save";
      /** What the real save must carry besides the term: the target, or `createNewContract`. */
      request: Pick<
        IApplyContractTermRequest,
        "targetContractFeeScheduleId" | "createNewContract"
      >;
    }
  /** An existing contract already has every field the term would fill: nothing to write. */
  | { kind: "up_to_date" }
  /** The term's payer has several existing contracts for this provider: she picks one. */
  | { kind: "needs_choice" };

/**
 * Where a term saves, from its fill-only dry run:
 *
 * - its payer is the run's (`requiresChoice` false): wherever the server put it — the run's contract, or,
 *   on a standalone run, a new draft (`createNewContract`);
 * - another payer, already saved into one of its contracts (`alreadySavedTo`): that contract again — she
 *   picked it once, so several candidates are no longer a question;
 * - another payer with NO existing contract for this provider: a new draft contract for that payer;
 * - another payer with exactly ONE: that contract;
 * - another payer with SEVERAL: skipped — which contract is her decision, not ours.
 *
 * Writing to an existing contract that already has every field is skipped as up to date; a new contract
 * always writes.
 */
export const saveDraftStep = (
  dryRun: IApplyContractTermResult
): SaveDraftStep => {
  const { target } = dryRun;
  const candidates = target.existingCandidates ?? [];
  const savedTo = target.alreadySavedTo ?? null;
  if (target.requiresChoice && savedTo) {
    // The server's no-choice dry run proposes this same contract, so its changes are measured against it.
    if (
      target.contractFeeScheduleId === savedTo &&
      !planWritesSomething(dryRun.changes)
    )
      return { kind: "up_to_date" };
    return { kind: "save", request: { targetContractFeeScheduleId: savedTo } };
  }
  if (target.requiresChoice && candidates.length > 1)
    return { kind: "needs_choice" };
  if (target.mode === "create")
    return { kind: "save", request: { createNewContract: true } };
  if (!planWritesSomething(dryRun.changes)) return { kind: "up_to_date" };
  if (target.requiresChoice) {
    const only = candidates[0]?.id ?? target.contractFeeScheduleId;
    return only
      ? { kind: "save", request: { targetContractFeeScheduleId: only } }
      : { kind: "save", request: { createNewContract: true } };
  }
  return { kind: "save", request: {} };
};

/** How one term came out. */
export type SaveDraftOutcome =
  | {
      kind: "saved" | "up_to_date";
      recId: string;
      payerName: string;
      contractId: string | null;
      contractName: string | null;
      created: boolean;
    }
  | { kind: "needs_choice"; recId: string; payerName: string }
  /** Left alone before any call (`saveDraftSkips`). */
  | { kind: SaveDraftSkipReason; recId: string; payerName: string }
  | { kind: "failed"; recId: string; payerName: string; message: string };

const errorMessage = (e: unknown): string => {
  const api = (e as { data?: { message?: unknown } })?.data?.message;
  if (typeof api === "string" && api) return api;
  if (e instanceof Error && e.message) return e.message;
  return "Couldn't save it.";
};

export interface ISaveDraftDeps {
  runId: string;
  accept: (request: IAcceptRequest) => Promise<unknown>;
  apply: (
    request: IApplyContractTermRequest
  ) => Promise<IApplyContractTermResult>;
}

/**
 * Save each term in turn (not in parallel: an accept refetches the run, and two creates for one payer
 * must not race). A failure on one term is recorded and the rest carry on.
 */
export const runSaveDraft = async (
  terms: ISaveDraftTerm[],
  { runId, accept, apply }: ISaveDraftDeps
): Promise<SaveDraftOutcome[]> => {
  const outcomes: SaveDraftOutcome[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const term of terms) {
    const base = { recId: term.id, payerName: term.payerName };
    try {
      if (term.accept) {
        // eslint-disable-next-line no-await-in-loop
        await accept({
          recommendationId: term.id,
          ...(term.edits ? { editedModeling: term.edits } : {}),
        });
      }
      // eslint-disable-next-line no-await-in-loop
      const dryRun = await apply({
        runId,
        recommendationId: term.id,
        dryRun: true,
        replace: false,
      });
      const step = saveDraftStep(dryRun);
      if (step.kind === "needs_choice") {
        outcomes.push({ kind: "needs_choice", ...base });
      } else if (step.kind === "up_to_date") {
        outcomes.push({
          kind: "up_to_date",
          ...base,
          contractId: dryRun.contractFeeScheduleId,
          contractName: dryRun.target.contractName ?? null,
          created: false,
        });
      } else {
        // eslint-disable-next-line no-await-in-loop
        const saved = await apply({
          runId,
          recommendationId: term.id,
          dryRun: false,
          replace: false,
          ...step.request,
        });
        outcomes.push({
          kind: saved.created || saved.applied ? "saved" : "up_to_date",
          ...base,
          contractId: saved.contractFeeScheduleId,
          contractName: saved.target.contractName ?? null,
          created: saved.created,
        });
      }
    } catch (e) {
      outcomes.push({ kind: "failed", ...base, message: errorMessage(e) });
    }
  }
  return outcomes;
};

// ---------------------------------------------------------------------------------------------
// What the toast says
// ---------------------------------------------------------------------------------------------

export interface ISaveDraftSummary {
  severity: "success" | "info" | "warn" | "error";
  /** "Saved 3 contract terms · SuperMed needs you to pick a contract". */
  summary: string;
  /** One line per term that failed: "ACA: <why>". */
  failures: string[];
  /** One line per term left alone on purpose: overridden, or still needing input. */
  notSaved: string[];
  /** The contracts saved to, one link each. */
  links: { contractId: string; label: string }[];
  /** The terms to highlight: their payer has several contracts to choose from. */
  needsChoiceIds: string[];
}

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** The toast's line for a term she overrode — Save draft never writes the proposal she disagreed with. */
export const overriddenNotSaved = (payerName: string): string =>
  `${payerName}: you overrode this term — save it from its card if you still want to`;

/** The toast's line for a term that still lists missing inputs. */
export const needsInputNotSaved = (payerName: string): string =>
  `${payerName}: needs input — fill in what it's missing on its card, then save it there`;

/** "SuperMed", "SuperMed and ACA", "SuperMed, ACA and Medflex". */
const listNames = (names: string[]): string =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

export const summarizeSaveDraft = (
  outcomes: SaveDraftOutcome[]
): ISaveDraftSummary => {
  const saved = outcomes.filter((o) => o.kind === "saved");
  const upToDate = outcomes.filter((o) => o.kind === "up_to_date");
  const choice = outcomes.filter((o) => o.kind === "needs_choice");
  const overridden = outcomes.filter((o) => o.kind === "overridden");
  const needsInput = outcomes.filter((o) => o.kind === "needs_input");
  const failed = outcomes.filter(
    (o): o is Extract<SaveDraftOutcome, { kind: "failed" }> =>
      o.kind === "failed"
  );

  const parts: string[] = [];
  if (saved.length > 0)
    parts.push(
      `Saved ${plural(saved.length, "contract term", "contract terms")}`
    );
  if (upToDate.length > 0)
    parts.push(
      saved.length > 0
        ? `${upToDate.length} already up to date`
        : `${plural(
            upToDate.length,
            "contract term",
            "contract terms"
          )} already up to date`
    );
  if (choice.length > 0) {
    const names = Array.from(new Set(choice.map((o) => o.payerName)));
    parts.push(
      `${listNames(names)} ${
        names.length === 1 ? "needs" : "need"
      } you to pick a contract`
    );
  }
  if (overridden.length > 0)
    parts.push(
      `${plural(
        overridden.length,
        "overridden term",
        "overridden terms"
      )} not saved`
    );
  if (needsInput.length > 0)
    parts.push(
      `${needsInput.length} ${needsInput.length === 1 ? "needs" : "need"} input`
    );
  if (failed.length > 0) parts.push(`${failed.length} couldn't be saved`);

  const links: { contractId: string; label: string }[] = [];
  [...saved, ...upToDate].forEach((o) => {
    if (o.kind !== "saved" && o.kind !== "up_to_date") return;
    if (!o.contractId || links.some((l) => l.contractId === o.contractId))
      return;
    links.push({
      contractId: o.contractId,
      label: o.contractName ?? o.payerName,
    });
  });

  let severity: ISaveDraftSummary["severity"] = "success";
  if (failed.length > 0)
    severity = saved.length + upToDate.length > 0 ? "warn" : "error";
  else if (choice.length + overridden.length + needsInput.length > 0)
    severity = "warn";
  else if (saved.length === 0) severity = "info";

  return {
    severity,
    summary: parts.length > 0 ? parts.join(" · ") : "No contract terms to save",
    failures: failed.map((o) => `${o.payerName}: ${o.message}`),
    notSaved: [
      ...overridden.map((o) => overriddenNotSaved(o.payerName)),
      ...needsInput.map((o) => needsInputNotSaved(o.payerName)),
    ],
    links,
    needsChoiceIds: choice.map((o) => o.recId),
  };
};
